import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Armchair,
  CalendarDays,
  Cake,
  Check,
  Clock,
  Lock,
  LogOut,
  Mail,
  MessageCircle,
  PartyPopper,
  Phone,
  Search,
  Settings as SettingsIcon,
  Sparkles,
  Trash2,
  Undo2,
  Utensils,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Calendar } from "../components/ui/calendar";

import { TopBar } from "../components/TopBar";
import { Logo } from "../components/Logo";
import { useApp } from "../context/AppContext";
import {
  fetchBookings,
  updateBookingStatus,
  editBooking,
  fetchAdminSettings,
  updateAdminSettings,
  reconcilePayments,
  markBookingPaid,
  deleteBooking,
  cleanupExpiredBookings,
  verifyAdmin,
  checkBookingStripe,
} from "../lib/api";
import { PaidCheckbox, CheckStripeButton, PaymentCountdown } from "../components/AdminPaymentControls";
import { DINNER_SLOT_LABELS, dinnerSlotLabel, ALL_DINNER_SLOTS } from "../lib/i18n";
import { formatISODate, todayISO, sortBookings, matchesTab, BOOKING_TABS, SORT_MODES } from "../lib/utils";

// ---------------------------------------------------------------------------

function AdminGate({ onSuccess }) {
  const { t } = useApp();
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await verifyAdmin(password);
      onSuccess(password);
    } catch {
      setErr(t("admin_wrong_pwd"));
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="mx-auto mt-16 max-w-sm animate-fade-slide-up"
      data-testid="admin-gate"
    >
      <Card className="border-border bg-card">
        <CardContent className="p-8 text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
            <Lock className="h-6 w-6" />
          </div>
          <div className="mx-auto mb-2 text-primary">
            <Logo size={22} />
          </div>
          <h2 className="font-display text-xl font-semibold">
            {t("admin_gate_title")}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("admin_gate_subtitle")}
          </p>
          <form onSubmit={submit} className="mt-6 space-y-3 text-left">
            <Label htmlFor="admin-password">{t("admin_password")}</Label>
            <Input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`h-11 ${shake ? "animate-shake" : ""}`}
              autoFocus
              data-testid="admin-password-input"
              autoComplete="current-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            {err && (
              <p
                className="text-sm text-destructive"
                role="alert"
                data-testid="admin-error"
              >
                {err}
              </p>
            )}
            <Button
              type="submit"
              className="h-11 w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={loading}
              data-testid="admin-enter-button"
            >
              {t("admin_enter")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------

function KpiBadge({ label, value, color, testId }) {
  return (
    <div
      className={`flex items-center gap-2 rounded-full border px-3 py-1.5 ${color}`}
      data-testid={testId}
    >
      <span className="font-display text-base font-semibold tabular-nums">
        {value}
      </span>
      <span className="text-xs uppercase tracking-wider">{label}</span>
    </div>
  );
}

function StatusBadge({ status, tLocal }) {
  const map = {
    pending: {
      label: tLocal("status_pending_short"),
      cls:
        "animate-pulse-glow border-primary/50 bg-primary/20 text-primary hover:bg-primary/25",
    },
    confirmed: {
      label: tLocal("status_confirmed_short"),
      cls:
        "border-[hsl(var(--success)/0.5)] bg-[hsl(var(--success)/0.18)] text-[hsl(var(--success))]",
    },
    rejected: {
      label: tLocal("status_rejected_short"),
      cls: "border-destructive/50 bg-destructive/15 text-destructive",
    },
    cancelled: {
      label: tLocal("status_cancelled_short"),
      cls: "border-muted-foreground/40 bg-muted/50 text-muted-foreground",
    },
  };
  const m = map[status] || map.pending;
  return (
    <Badge
      role="status"
      aria-label={m.label}
      className={`transition-colors ${m.cls}`}
      data-testid={`status-badge-${status}`}
    >
      {m.label}
    </Badge>
  );
}

function Avatar({ nome, cognome }) {
  const initials =
    (nome?.[0] || "").toUpperCase() + (cognome?.[0] || "").toUpperCase();
  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 font-display text-base font-semibold text-primary">
      {initials || "?"}
    </div>
  );
}

function PaymentBadge({ booking }) {
  const ps = booking.payment_status || "none";
  if (ps === "none") return null;
  const map = {
    pending: { label: "PAY · PENDING", cls: "border-primary/50 bg-primary/15 text-primary" },
    paid: { label: "PAID ✓", cls: "border-[hsl(var(--success)/0.5)] bg-[hsl(var(--success)/0.18)] text-[hsl(var(--success))]" },
    failed: { label: "PAY · FAILED", cls: "border-destructive/50 bg-destructive/15 text-destructive" },
    expired: { label: "PAY · EXPIRED", cls: "border-muted-foreground/40 bg-muted/40 text-muted-foreground" },
  };
  const m = map[ps];
  if (!m) return null;
  return (
    <Badge className={`gap-1 ${m.cls}`} data-testid={`payment-badge-${booking.id}`}>
      {m.label}
      {booking.payment_amount != null && ` · €${booking.payment_amount}`}
    </Badge>
  );
}

function BookingCard({ booking, onConfirm, onReject, onReset, onEdit, onMarkPaid, onDelete, onCheckStripe }) {
  const { t } = useApp();
  const [confirmingReject, setConfirmingReject] = useState(false);
  const [confirmingEvent, setConfirmingEvent] = useState(false);
  const [eventDeposit, setEventDeposit] = useState("");
  const [editingDeposit, setEditingDeposit] = useState(false);
  const [depositDraft, setDepositDraft] = useState(String(booking.deposit ?? ""));
  const [editingMessage, setEditingMessage] = useState(false);
  const [messageDraft, setMessageDraft] = useState(booking.custom_message || "");

  const noteSnippet = booking.note
    ? booking.note.length > 60
      ? booking.note.slice(0, 60) + "…"
      : booking.note
    : "";

  const handleConfirmClick = () => {
    if (booking.service === "event") {
      setConfirmingEvent(true);
    } else {
      // Confirm using the persisted custom_message (set via the dedicated editor)
      onConfirm(booking, undefined, booking.custom_message || null);
    }
  };

  const submitEventConfirm = () => {
    const amt = parseInt(eventDeposit, 10);
    if (!amt || amt < 1) return;
    onConfirm(booking, amt, booking.custom_message || null);
    setConfirmingEvent(false);
    setEventDeposit("");
  };

  const submitDepositEdit = async () => {
    const amt = parseInt(depositDraft, 10);
    if (isNaN(amt) || amt < 0) return;
    const ok = await onEdit(booking, { deposit: amt });
    if (ok) setEditingDeposit(false);
  };

  const submitMessageEdit = async () => {
    const ok = await onEdit(booking, { custom_message: messageDraft });
    if (ok) setEditingMessage(false);
  };

  return (
    <Card
      className="animate-fade-slide-up border-border bg-card transition-colors"
      data-testid={`booking-card-${booking.id}`}
    >
      <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:gap-5 sm:p-5">
        <Avatar nome={booking.nome} cognome={booking.cognome} />

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-display text-base font-semibold text-foreground">
              {booking.nome} {booking.cognome}
            </p>
            {booking.instagram && (
              <Badge
                variant="outline"
                className="border-primary/40 text-primary"
              >
                {booking.instagram}
              </Badge>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <Badge variant="outline" className="border-primary/50 bg-primary/10 text-primary">
              <Utensils className="mr-1 h-3 w-3" />
              {t(`service_${booking.service || "dinner"}`)}
            </Badge>
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5" />
              {formatISODate(booking.date)}
            </span>
            {booking.time && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {booking.service === "dinner"
                  ? dinnerSlotLabel(booking.date, booking.time)
                  : booking.time}
              </span>
            )}
            {booking.service === "event" && booking.event_fascia && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {t(`fascia_${booking.event_fascia}`)}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Armchair className="h-3.5 w-3.5" />
              {booking.guests}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            {booking.email && (
              <a
                href={`mailto:${booking.email}`}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-background/60 px-2.5 py-1 text-xs text-foreground/90 transition-colors hover:border-primary/60 hover:bg-primary/10 hover:text-primary"
                data-testid={`contact-email-${booking.id}`}
              >
                <Mail className="h-3 w-3" />
                <span className="max-w-[180px] truncate">{booking.email}</span>
              </a>
            )}
            {booking.phone && (
              <>
                <a
                  href={`tel:+${booking.phone.replace(/^\+/, "")}`}
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-background/60 px-2.5 py-1 text-xs text-foreground/90 transition-colors hover:border-primary/60 hover:bg-primary/10 hover:text-primary"
                  data-testid={`contact-phone-${booking.id}`}
                >
                  <Phone className="h-3 w-3" />
                  +{booking.phone.replace(/^\+/, "")}
                </a>
                <a
                  href={`https://wa.me/${booking.phone.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-full border border-[#25D366]/60 bg-[#25D366]/10 px-2.5 py-1 text-xs text-[#25D366] transition-colors hover:bg-[#25D366]/20"
                  data-testid={`contact-whatsapp-${booking.id}`}
                >
                  <MessageCircle className="h-3 w-3" />
                  WhatsApp
                </a>
              </>
            )}
          </div>

          {(booking.occasion || (booking.allergies && booking.allergies.length) || booking.birthday) && (
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              {booking.occasion && (
                <Badge variant="outline" className="gap-1 border-primary/40 text-primary">
                  <PartyPopper className="h-3 w-3" />
                  {t(`occasion_${booking.occasion}`)}
                  {booking.occasion === "other" && booking.occasion_note ? `: ${booking.occasion_note}` : ""}
                </Badge>
              )}
              {(booking.allergies || []).map((a) => {
                const n = (booking.allergy_counts || {})[a];
                return (
                  <Badge key={a} variant="outline" className="border-destructive/40 text-destructive">
                    {t(`allergy_${a}`)}
                    {n ? (
                      <span className="ml-1 font-semibold tabular-nums">
                        ×{n}
                      </span>
                    ) : null}
                  </Badge>
                );
              })}
              {booking.birthday && (
                <Badge variant="outline" className="gap-1 border-border text-muted-foreground">
                  <Cake className="h-3 w-3" />
                  {formatISODate(booking.birthday)}
                </Badge>
              )}
            </div>
          )}
          {booking.allergies_note && (
            <p className="text-xs italic text-muted-foreground">⚠ {booking.allergies_note}</p>
          )}

          {booking.service === "event" && (
            <div className="space-y-1.5 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
              {booking.event_type && (
                <div>
                  <span className="text-muted-foreground">{t("event_field_type")}:</span>{" "}
                  <span className="font-medium text-foreground">
                    {t(`event_type_${booking.event_type}`) || booking.event_type}
                    {booking.event_type === "other" && booking.event_type_other
                      ? ` — ${booking.event_type_other}`
                      : ""}
                  </span>
                </div>
              )}
              {booking.event_description && (
                <p className="italic text-muted-foreground">&ldquo;{booking.event_description}&rdquo;</p>
              )}
              {booking.event_setup && booking.event_setup.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {booking.event_setup.map((s) => (
                    <Badge key={s} variant="outline" className="border-primary/40 text-primary">
                      {t(`setup_${s}`)}
                    </Badge>
                  ))}
                </div>
              )}
              {booking.event_budget != null && (
                <div className="text-xs text-muted-foreground">
                  Budget: <span className="font-medium text-primary">€{booking.event_budget}/pers.</span>
                </div>
              )}
            </div>
          )}

          {noteSnippet && (
            <p className="text-sm italic text-muted-foreground">
              “{noteSnippet}”
            </p>
          )}

          {/* Deposit — editable inline (hidden if event with deposit 0 or already paid) */}
          {booking.payment_status !== "paid" && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-muted-foreground">{t("summary_deposit")}:</span>
              {editingDeposit ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="relative">
                    <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                    <Input
                      type="number"
                      min={0}
                      max={10000}
                      step="1"
                      value={depositDraft}
                      onChange={(e) => setDepositDraft(e.target.value)}
                      className="h-7 w-24 pl-6 text-sm"
                      autoFocus
                      data-testid={`deposit-input-${booking.id}`}
                    />
                  </span>
                  <Button
                    size="sm"
                    className="h-7 bg-[hsl(var(--success))] px-2 text-white hover:bg-[hsl(var(--success)/0.9)]"
                    onClick={submitDepositEdit}
                    data-testid={`deposit-save-${booking.id}`}
                  >
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={() => { setEditingDeposit(false); setDepositDraft(String(booking.deposit ?? "")); }}
                    data-testid={`deposit-cancel-${booking.id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditingDeposit(true)}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                  data-testid={`deposit-edit-${booking.id}`}
                  title={t("edit_deposit_tooltip")}
                >
                  €{booking.deposit}
                </button>
              )}
            </div>
          )}

          {/* Custom message — visible/editable for any not-yet-paid pending booking */}
          {booking.status === "pending" && booking.payment_status !== "paid" && (
            <div className="space-y-1">
              {editingMessage ? (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t("custom_message_label")}
                  </Label>
                  <Textarea
                    value={messageDraft}
                    onChange={(e) => setMessageDraft(e.target.value)}
                    maxLength={2000}
                    rows={3}
                    placeholder={t("custom_message_placeholder")}
                    data-testid={`message-input-${booking.id}`}
                    className="text-sm"
                  />
                  <div className="flex justify-end gap-1.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7"
                      onClick={() => { setEditingMessage(false); setMessageDraft(booking.custom_message || ""); }}
                      data-testid={`message-cancel-${booking.id}`}
                    >
                      {t("action_cancel")}
                    </Button>
                    <Button
                      size="sm"
                      className="h-7 bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success)/0.9)]"
                      onClick={submitMessageEdit}
                      data-testid={`message-save-${booking.id}`}
                    >
                      {t("action_save")}
                    </Button>
                  </div>
                </div>
              ) : booking.custom_message ? (
                <button
                  type="button"
                  onClick={() => { setEditingMessage(true); setMessageDraft(booking.custom_message || ""); }}
                  className="block w-full rounded-md border border-primary/30 bg-primary/5 p-2 text-left text-xs italic text-primary hover:bg-primary/10"
                  data-testid={`message-edit-${booking.id}`}
                >
                  ✉ &ldquo;{booking.custom_message}&rdquo;
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditingMessage(true)}
                  className="text-xs text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
                  data-testid={`message-add-${booking.id}`}
                >
                  + {t("custom_message_add")}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
          <StatusBadge status={booking.status} tLocal={t} />
          <PaymentBadge booking={booking} />
          <PaymentCountdown booking={booking} />
          <PaidCheckbox booking={booking} onMarkPaid={onMarkPaid} />
          <CheckStripeButton booking={booking} onCheck={onCheckStripe} />

          {booking.status === "pending" && !confirmingReject && !confirmingEvent && (
            <div className="flex gap-2">
              <Button
                size="sm"
                className="h-9 gap-1.5 rounded-full bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success)/0.9)]"
                onClick={handleConfirmClick}
                data-testid={`confirm-button-${booking.id}`}
              >
                <Check className="h-4 w-4" />
                {t("action_confirm")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-9 gap-1.5 rounded-full border-destructive/50 text-destructive hover:bg-destructive/10"
                onClick={() => setConfirmingReject(true)}
                data-testid={`reject-button-${booking.id}`}
              >
                <X className="h-4 w-4" />
                {t("action_reject")}
              </Button>
            </div>
          )}

          {confirmingEvent && (
            <div className="w-[260px] rounded-md border border-primary/50 bg-primary/5 p-3 text-right" data-testid={`event-confirm-${booking.id}`}>
              <p className="mb-2 text-left text-xs text-muted-foreground">
                {t("event_confirm_amount_label")}
              </p>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                <Input
                  type="number"
                  min={1}
                  max={10000}
                  step="1"
                  value={eventDeposit}
                  onChange={(e) => setEventDeposit(e.target.value)}
                  className="pl-7"
                  placeholder="150"
                  autoFocus
                  data-testid={`event-deposit-input-${booking.id}`}
                />
              </div>
              <div className="mt-2 flex justify-end gap-1.5">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setConfirmingEvent(false); setEventDeposit(""); }}
                  data-testid={`event-confirm-cancel-${booking.id}`}
                >
                  {t("action_cancel")}
                </Button>
                <Button
                  size="sm"
                  className="bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success)/0.9)]"
                  onClick={submitEventConfirm}
                  disabled={!eventDeposit || parseInt(eventDeposit, 10) < 1}
                  data-testid={`event-confirm-submit-${booking.id}`}
                >
                  {t("event_confirm_submit")}
                </Button>
              </div>
            </div>
          )}

          {booking.status === "pending" && confirmingReject && (
            <div className="max-w-[240px] rounded-md border border-border bg-background p-2 text-right">
              <p className="mb-2 text-xs text-muted-foreground">
                {t("reject_confirm_text")}
              </p>
              <div className="flex justify-end gap-1.5">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmingReject(false)}
                  data-testid={`reject-cancel-${booking.id}`}
                >
                  {t("action_cancel")}
                </Button>
                <Button
                  size="sm"
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => {
                    onReject(booking);
                    setConfirmingReject(false);
                  }}
                  data-testid={`reject-confirm-${booking.id}`}
                >
                  {t("reject_yes")}
                </Button>
              </div>
            </div>
          )}

          {["confirmed", "rejected", "cancelled"].includes(booking.status) && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
              onClick={() => onReset(booking)}
              data-testid={`reset-button-${booking.id}`}
            >
              <Undo2 className="h-3.5 w-3.5" />
              {t("action_reset")}
            </Button>
          )}

          <Button
            size="sm"
            variant="ghost"
            className="h-8 gap-1.5 text-destructive/70 hover:bg-destructive/10 hover:text-destructive"
            onClick={() => onDelete && onDelete(booking)}
            data-testid={`delete-button-${booking.id}`}
            title={t("delete_booking_tooltip")}
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t("action_delete")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  const { t } = useApp();
  return (
    <div
      className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-16 text-center"
      data-testid="empty-state"
    >
      <div className="relative mb-4">
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary animate-pulse-glow">
          <Armchair className="h-6 w-6" />
        </span>
        <span className="absolute -right-2 -top-1 h-2 w-2 rounded-full bg-primary shadow-[0_0_12px_3px_hsl(var(--primary)/0.6)] animate-firefly-float" />
      </div>
      <h3 className="font-display text-lg font-semibold">
        {t("empty_title")}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {t("empty_subtitle")}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Dashboard({ password, onLogout }) {
  const { t } = useApp();
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sortMode, setSortMode] = useState("upcoming");
  const [loading, setLoading] = useState(true);

  // New filters
  const [serviceFilter, setServiceFilter] = useState("all"); // all | reservations | events
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // list | calendar
  const [calendarDate, setCalendarDate] = useState(new Date());

  const load = useCallback(async () => {
    try {
      const data = await fetchBookings(password);
      setBookings(data);
    } catch (err) {
      // Persisted password no longer valid → force re-login
      if (err?.response?.status === 401) {
        onLogout();
        return;
      }
      toast.error("Error loading bookings");
    } finally {
      setLoading(false);
    }
  }, [password, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let mounted = true;
    fetchAdminSettings(password)
      .then((d) => { if (mounted) { setSettings(d); setCapacityDraft(d.dinner_capacity || {}); setLunchCapacityDraft(String(d.lunch_capacity ?? "")); } })
      .catch(() => {});
    return () => { mounted = false; };
  }, [password]);

  const [capacityDraft, setCapacityDraft] = useState({});
  const [lunchCapacityDraft, setLunchCapacityDraft] = useState("");
  const [savingCapacity, setSavingCapacity] = useState(false);

  const saveSettings = async (newDeposit) => {
    try {
      const updated = await updateAdminSettings({ deposit_per_person: newDeposit }, password);
      setSettings((s) => ({ ...s, ...updated }));
      toast.success(t("toast_saved"));
    } catch {
      toast.error("Error");
    }
  };

  const saveCapacity = async () => {
    setSavingCapacity(true);
    try {
      const dinner_capacity = Object.fromEntries(
        Object.entries(capacityDraft).map(([slot, v]) => [slot, Number(v)])
      );
      const lunch_capacity = Number(lunchCapacityDraft) || undefined;
      const updated = await updateAdminSettings({ dinner_capacity, lunch_capacity }, password);
      setSettings((s) => ({ ...s, ...updated }));
      setCapacityDraft(updated.dinner_capacity || {});
      setLunchCapacityDraft(String(updated.lunch_capacity ?? ""));
      toast.success(t("toast_saved"));
    } catch {
      toast.error("Error");
    } finally {
      setSavingCapacity(false);
    }
  };

  const markPaidHandler = async (booking) => {
    if (!window.confirm(t("mark_paid_confirm"))) return;
    try {
      const updated = await markBookingPaid(booking.id, password);
      setBookings((list) => list.map((b) => (b.id === booking.id ? updated : b)));
      toast.success(t("toast_marked_paid"));
    } catch {
      toast.error("Error");
    }
  };

  const handleCheckStripe = async (booking) => {
    try {
      const updated = await checkBookingStripe(booking.id, password);
      setBookings((list) => list.map((b) => (b.id === booking.id ? updated : b)));
      toast.success(t(updated.payment_status === "paid" ? "toast_stripe_paid" : "toast_stripe_unpaid"));
    } catch {
      toast.error(t("toast_stripe_unreachable"));
    }
  };

  const handleDelete = async (booking) => {
    if (!window.confirm(t("delete_confirm_text"))) return;
    try {
      await deleteBooking(booking.id, password);
      setBookings((list) => list.filter((b) => b.id !== booking.id));
      toast.success(t("toast_saved"));
    } catch {
      toast.error("Error");
    }
  };

  const [cleaningUp, setCleaningUp] = useState(false);
  const handleCleanupExpired = async () => {
    if (!window.confirm(t("cleanup_expired_confirm"))) return;
    setCleaningUp(true);
    try {
      await cleanupExpiredBookings(password);
      await load();
      toast.success(t("toast_saved"));
    } catch {
      toast.error("Error");
    } finally {
      setCleaningUp(false);
    }
  };

  const [reconciling, setReconciling] = useState(false);
  const handleReconcile = async () => {
    if (reconciling) return;
    setReconciling(true);
    try {
      const res = await reconcilePayments(password);
      if (res.finalized > 0) {
        toast.success(`${res.finalized} ${t("toast_reconcile_finalized")}`);
        load();
      } else {
        toast.success(`${t("toast_reconcile_none")} (${res.checked} ${t("toast_reconcile_checked")})`);
      }
    } catch {
      toast.error("Error");
    } finally {
      setReconciling(false);
    }
  };

  const kpis = useMemo(() => {
    const k = { pending: 0, confirmed: 0, rejected: 0, cancelled: 0, paid: 0, awaiting_payment: 0, events_pending: 0 };
    bookings.forEach((b) => {
      k[b.status] = (k[b.status] || 0) + 1;
      if (matchesTab(b, "paid")) k.paid += 1;
      if (matchesTab(b, "awaiting_payment")) k.awaiting_payment += 1;
      if (b.service === "event" && b.status === "pending") {
        k.events_pending += 1;
      }
    });
    return k;
  }, [bookings]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bookings.filter((b) => {
      // Status tab
      if (!matchesTab(b, filter)) return false;
      // Service filter
      if (serviceFilter === "events" && b.service !== "event") return false;
      if (serviceFilter === "dinner" && b.service !== "dinner") return false;
      if (serviceFilter === "lunch" && b.service !== "lunch") return false;
      // Date range (matches booking.date YYYY-MM-DD)
      if (dateFrom && b.date < dateFrom) return false;
      if (dateTo && b.date > dateTo) return false;
      // Search across name/handle/email
      if (q) {
        const haystack = [
          b.nome,
          b.cognome,
          b.instagram,
          b.email,
          b.phone,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [bookings, filter, serviceFilter, search, dateFrom, dateTo]);

  const sorted = useMemo(() => sortBookings(filtered, sortMode), [filtered, sortMode]);

  const daysWithBookings = useMemo(() => {
    const set = new Set();
    bookings.forEach((b) => { if (b.date) set.add(b.date); });
    return [...set].map((iso) => {
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, m - 1, d);
    });
  }, [bookings]);

  const calendarDayISO = useMemo(() => {
    const y = calendarDate.getFullYear();
    const m = String(calendarDate.getMonth() + 1).padStart(2, "0");
    const d = String(calendarDate.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [calendarDate]);

  const calendarDayBookings = useMemo(() => {
    return bookings
      .filter((b) => b.date === calendarDayISO)
      .slice()
      .sort((a, b) => {
        const t1 = (a.time || "").localeCompare(b.time || "");
        if (t1 !== 0) return t1;
        return (a.createdAt || "").localeCompare(b.createdAt || "");
      });
  }, [bookings, calendarDayISO]);

  const hasActiveFilters =
    serviceFilter !== "all" || search.trim() || dateFrom || dateTo;

  const clearFilters = () => {
    setServiceFilter("all");
    setSearch("");
    setDateFrom("");
    setDateTo("");
  };

  const updateStatus = async (booking, status, msgKey, depositAmount, customMessage) => {
    // optimistic
    const prev = bookings;
    setBookings((list) =>
      list.map((b) => (b.id === booking.id ? { ...b, status, ...(depositAmount != null ? { deposit: depositAmount } : {}), ...(customMessage != null ? { custom_message: customMessage } : {}) } : b))
    );
    try {
      const updated = await updateBookingStatus(booking.id, status, password, depositAmount, customMessage);
      setBookings((list) => list.map((b) => (b.id === booking.id ? updated : b)));
      toast.success(`${t(msgKey)} ${booking.nome}`);
    } catch {
      setBookings(prev);
      toast.error("Error");
    }
  };

  const editBookingFields = async (booking, fields) => {
    try {
      const updated = await editBooking(booking.id, fields, password);
      setBookings((list) => list.map((b) => (b.id === booking.id ? updated : b)));
      toast.success(t("toast_saved"));
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail?.code;
      toast.error(code === "already_paid" ? t("toast_edit_locked_paid") : "Error");
      return false;
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-xl font-semibold">
              {t("admin_title")}
            </h1>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSettingsOpen((v) => !v)}
              className="gap-1.5"
              data-testid="admin-settings-toggle"
              aria-expanded={settingsOpen}
            >
              <SettingsIcon className="h-4 w-4" />
              {t("admin_settings")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="gap-1.5"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4" />
              {t("admin_logout")}
            </Button>
          </div>
        </div>

        {/* Settings panel */}
        {settingsOpen && (
          <Card className="mt-4 border-primary/30 bg-primary/5" data-testid="admin-settings-panel">
            <CardContent className="space-y-3 p-5">
              <div>
                <p className="font-display text-base font-semibold">{t("settings_deposit_label")}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{t("settings_deposit_hint")}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {[1, 5, 10].map((v) => {
                  const active = (settings?.deposit_per_person ?? 10) === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => saveSettings(v)}
                      data-testid={`settings-deposit-chip-${v}`}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm transition-all ${
                        active
                          ? "border-primary bg-primary text-primary-foreground shadow-[0_0_0_4px_hsl(var(--primary)/0.18)]"
                          : "border-border bg-background/70 text-foreground hover:border-primary/60 hover:bg-primary/10 hover:text-primary"
                      }`}
                    >
                      €{v} {t("settings_deposit_per_person_suffix")}
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 border-t border-primary/20 pt-4">
                <p className="font-display text-base font-semibold">{t("settings_capacity_label")}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{t("settings_capacity_hint")}</p>
                <div className="mt-3 flex flex-wrap gap-3">
                  {ALL_DINNER_SLOTS.map((slot) => (
                    <div key={slot} className="flex flex-col gap-1">
                      <Label htmlFor={`capacity-${slot}`} className="text-xs text-muted-foreground">
                        {slot}
                      </Label>
                      <Input
                        id={`capacity-${slot}`}
                        type="number"
                        min={1}
                        max={500}
                        value={capacityDraft[slot] ?? ""}
                        onChange={(e) =>
                          setCapacityDraft((d) => ({ ...d, [slot]: e.target.value }))
                        }
                        className="w-24"
                        data-testid={`settings-capacity-input-${slot}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex flex-col gap-1">
                  <Label htmlFor="capacity-lunch" className="text-xs text-muted-foreground">
                    {t("settings_lunch_capacity_label")}
                  </Label>
                  <Input
                    id="capacity-lunch"
                    type="number"
                    min={1}
                    max={500}
                    value={lunchCapacityDraft}
                    onChange={(e) => setLunchCapacityDraft(e.target.value)}
                    className="w-24"
                    data-testid="settings-lunch-capacity-input"
                  />
                  <p className="text-xs text-muted-foreground">{t("settings_lunch_capacity_hint")}</p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-3 gap-2"
                  onClick={saveCapacity}
                  disabled={savingCapacity}
                  data-testid="settings-capacity-save-button"
                >
                  {savingCapacity ? "…" : t("settings_capacity_save")}
                </Button>
              </div>

              <div className="mt-4 border-t border-primary/20 pt-4">
                <p className="font-display text-base font-semibold">{t("settings_reconcile_label")}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{t("settings_reconcile_hint")}</p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-3 gap-2"
                  onClick={handleReconcile}
                  disabled={reconciling}
                  data-testid="settings-reconcile-button"
                >
                  {reconciling ? "…" : t("settings_reconcile_action")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPIs */}
        <div
          className="mt-5 flex flex-wrap gap-2"
          data-testid="admin-kpis"
        >
          {loading ? (
            <>
              <div className="h-8 w-28 animate-pulse rounded-full bg-muted" />
              <div className="h-8 w-28 animate-pulse rounded-full bg-muted" />
              <div className="h-8 w-28 animate-pulse rounded-full bg-muted" />
              <div className="h-8 w-32 animate-pulse rounded-full bg-muted" />
            </>
          ) : (
            <>
              <KpiBadge
                label={t("kpi_pending")}
                value={kpis.pending}
                color="border-primary/40 bg-primary/10 text-primary"
                testId="kpi-pending"
              />
              <KpiBadge
                label={t("kpi_confirmed")}
                value={kpis.confirmed}
                color="border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]"
                testId="kpi-confirmed"
              />
              <KpiBadge
                label={t("kpi_rejected")}
                value={kpis.rejected}
                color="border-destructive/40 bg-destructive/10 text-destructive"
                testId="kpi-rejected"
              />
              <KpiBadge
                label={t("kpi_awaiting_payment")}
                value={kpis.awaiting_payment}
                color="border-primary/40 bg-primary/5 text-primary"
                testId="kpi-awaiting-payment"
              />
              <KpiBadge
                label={t("kpi_paid")}
                value={kpis.paid}
                color="border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.08)] text-[hsl(var(--success))]"
                testId="kpi-paid"
              />
              <KpiBadge
                label={t("kpi_cancelled")}
                value={kpis.cancelled}
                color="border-border bg-muted/40 text-muted-foreground"
                testId="kpi-cancelled"
              />
              <button
                type="button"
                onClick={() => {
                  setServiceFilter("events");
                  setFilter("pending");
                }}
                data-testid="kpi-events-pending"
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 transition-all hover:scale-105 ${
                  kpis.events_pending > 0
                    ? "animate-pulse-glow border-primary/60 bg-primary/15 text-primary"
                    : "border-border bg-muted/40 text-muted-foreground"
                }`}
              >
                <Sparkles className="h-3.5 w-3.5" />
                <span className="font-display text-base font-semibold tabular-nums">
                  {kpis.events_pending}
                </span>
                <span className="text-xs uppercase tracking-wider">
                  {t("kpi_events_pending")}
                </span>
              </button>
            </>
          )}
        </div>

        {/* Search + filters */}
        <div className="mt-6 space-y-3" data-testid="admin-filters">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("filter_search_placeholder")}
              className="h-10 pl-9"
              data-testid="filter-search-input"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
            />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            {/* Service filter chips */}
            <div className="flex flex-col gap-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                {t("filter_service_label")}
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { v: "all", l: t("filter_service_all") },
                  { v: "dinner", l: t("filter_service_dinner") },
                  { v: "lunch", l: t("filter_service_lunch") },
                  { v: "events", l: t("filter_service_events") },
                ].map((opt) => {
                  const active = serviceFilter === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setServiceFilter(opt.v)}
                      data-testid={`filter-service-${opt.v}`}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-all ${
                        active
                          ? "border-primary bg-primary text-primary-foreground shadow-[0_0_0_4px_hsl(var(--primary)/0.18)]"
                          : "border-border bg-background/70 text-foreground hover:border-primary/60 hover:bg-primary/10 hover:text-primary"
                      }`}
                    >
                      {opt.v === "events" && <Sparkles className="h-3 w-3" />}
                      {opt.l}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date range */}
            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-1">
                <Label
                  htmlFor="filter-date-from"
                  className="text-xs uppercase tracking-wider text-muted-foreground"
                >
                  {t("filter_date_from")}
                </Label>
                <Input
                  id="filter-date-from"
                  type="date"
                  lang="it"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-10 w-[160px]"
                  data-testid="filter-date-from"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label
                  htmlFor="filter-date-to"
                  className="text-xs uppercase tracking-wider text-muted-foreground"
                >
                  {t("filter_date_to")}
                </Label>
                <Input
                  id="filter-date-to"
                  type="date"
                  lang="it"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-10 w-[160px]"
                  data-testid="filter-date-to"
                />
              </div>
            </div>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-10 gap-1.5 text-muted-foreground hover:text-destructive"
                data-testid="filter-clear-button"
              >
                <X className="h-3.5 w-3.5" />
                {t("filter_clear")}
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList className="h-auto flex-wrap justify-start bg-muted/60">
                {BOOKING_TABS.map((tab) => (
                  <TabsTrigger key={tab} value={tab} data-testid={`tab-${tab}`}>
                    {t(`tab_${tab}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <label className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              {t("sort_label")}
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm normal-case tracking-normal text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="sort-select"
              >
                {SORT_MODES.map((m) => (
                  <option key={m} value={m}>{t(`sort_${m}`)}</option>
                ))}
              </select>
            </label>
            <Tabs value={viewMode} onValueChange={setViewMode}>
              <TabsList className="bg-muted/60">
                <TabsTrigger value="list" data-testid="view-mode-list">
                  {t("view_list_label")}
                </TabsTrigger>
                <TabsTrigger value="calendar" data-testid="view-mode-calendar">
                  {t("view_calendar_label")}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-muted-foreground hover:text-destructive"
              onClick={handleCleanupExpired}
              disabled={cleaningUp}
              data-testid="cleanup-expired-button"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {cleaningUp ? "…" : t("cleanup_expired_button")}
            </Button>
            {!loading && viewMode === "list" && (
              <span
                className="text-xs text-muted-foreground"
                data-testid="filter-results-count"
              >
                {sorted.length} {t("filter_results_count")}
              </span>
            )}
          </div>
        </div>

        {/* List or Calendar view */}
        {viewMode === "calendar" ? (
          <div className="mt-5 flex flex-col gap-5 lg:flex-row" data-testid="calendar-view">
            <Card className="w-fit shrink-0 border-border/60 bg-card/60">
              <CardContent className="p-3">
                <Calendar
                  mode="single"
                  selected={calendarDate}
                  onSelect={(d) => d && setCalendarDate(d)}
                  modifiers={{ hasBookings: daysWithBookings }}
                  modifiersClassNames={{ hasBookings: "font-bold text-primary underline" }}
                  data-testid="admin-calendar"
                />
              </CardContent>
            </Card>
            <div className="flex-1 space-y-3" data-testid="calendar-day-list">
              <p className="text-sm text-muted-foreground">
                {formatISODate(calendarDayISO)} — {calendarDayBookings.length} {t("filter_results_count")}
              </p>
              {calendarDayBookings.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  {t("calendar_empty_day")}
                </p>
              ) : (
                calendarDayBookings.map((b) => (
                  <BookingCard
                    key={b.id}
                    booking={b}
                    onConfirm={(bk, depositAmount, customMessage) =>
                      updateStatus(bk, "confirmed", "toast_confirmed", depositAmount, customMessage)
                    }
                    onReject={(bk) => updateStatus(bk, "rejected", "toast_rejected")}
                    onReset={(bk) => updateStatus(bk, "pending", "toast_reset")}
                    onEdit={editBookingFields}
                    onMarkPaid={markPaidHandler}
                    onDelete={handleDelete}
                    onCheckStripe={handleCheckStripe}
                  />
                ))
              )}
            </div>
          </div>
        ) : (
        <div className="mt-5 space-y-3" data-testid="bookings-list">
          {loading ? (
            <div className="py-20 text-center text-sm text-muted-foreground">
              …
            </div>
          ) : sorted.length === 0 ? (
            <EmptyState />
          ) : (
            sorted.map((b) => (
              <BookingCard
                key={b.id}
                booking={b}
                onConfirm={(bk, depositAmount, customMessage) =>
                  updateStatus(bk, "confirmed", "toast_confirmed", depositAmount, customMessage)
                }
                onReject={(bk) =>
                  updateStatus(bk, "rejected", "toast_rejected")
                }
                onReset={(bk) =>
                  updateStatus(bk, "pending", "toast_reset")
                }
                onEdit={editBookingFields}
                onMarkPaid={markPaidHandler}
                onDelete={handleDelete}
                onCheckStripe={handleCheckStripe}
              />
            ))
          )}
        </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export default function AdminPage() {
  const { adminPassword, setAdminPassword } = useApp();
  const navigate = useNavigate();

  const logout = () => {
    setAdminPassword(null);
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background">
      <TopBar showAdminButton={false} />
      {!adminPassword ? (
        <AdminGate onSuccess={(pwd) => setAdminPassword(pwd)} />
      ) : (
        <Dashboard password={adminPassword} onLogout={logout} />
      )}
    </div>
  );
}
